import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, GraduationCap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const NavbarHeader = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/", testId: "nav-home-link" },
    { name: "About", path: "/about", testId: "nav-about-link" },
    { name: "Programs", path: "/programs", testId: "nav-programs-link" },
    { name: "1:1 Coaching", path: "/coaching", testId: "nav-coaching-link" },
    { name: "Free Resources", path: "/free-resources", testId: "nav-free-resources-link" },
    { name: "Contact", path: "/contact", testId: "nav-contact-link" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full glassmorphism transition-all duration-300 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-20 flex items-center justify-between">
        {/* Brand Identity Logo */}
        <Link 
          to="/" 
          className="flex flex-col focus:outline-none group"
          data-testid="brand-logo-link"
        >
          <span className="font-display text-xl sm:text-2xl font-bold tracking-tight text-white group-hover:text-gold transition-colors duration-200">
            SUHASINI BASA
          </span>
          <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400 group-hover:text-slate-300 font-mono">
            Career Transformation
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={link.testId}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                isActive(link.path) 
                  ? "text-gold border-b-2 border-gold pb-1" 
                  : "text-slate-300 hover:text-white"
              }`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center space-x-4">
          <a
            href="https://academy.suhasinibasa.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="academy-portal-link"
            className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-slate-300 hover:text-gold transition-colors duration-200"
          >
            <GraduationCap className="w-4 h-4 text-gold" />
            <span>Academy</span>
            <ArrowUpRight className="w-3 h-3 opacity-60" />
          </a>
          <Link
            to="/start-here"
            data-testid="nav-start-here-button"
            className="bg-gold hover:bg-gold-hover text-obsidian px-5 py-2.5 rounded-none font-display font-bold text-xs uppercase tracking-widest transition-transform duration-200 active:scale-95"
          >
            START HERE
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle Menu"
          className="lg:hidden p-2 text-slate-300 hover:text-white focus:outline-none"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden border-t border-white/5 bg-navy/95 backdrop-blur-xl"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-${link.testId}`}
                  onClick={() => setIsOpen(false)}
                  className={`block text-base font-medium tracking-wide py-2 ${
                    isActive(link.path) ? "text-gold pl-2 border-l-2 border-gold" : "text-slate-300"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/5 flex flex-col gap-4">
                <a
                  href="https://academy.suhasinibasa.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-academy-portal-link"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-gold"
                >
                  <GraduationCap className="w-5 h-5 text-gold" />
                  <span>Academy Portal</span>
                  <ArrowUpRight className="w-4 h-4 opacity-60" />
                </a>
                <Link
                  to="/start-here"
                  data-testid="mobile-nav-start-here-button"
                  onClick={() => setIsOpen(false)}
                  className="bg-gold hover:bg-gold-hover text-obsidian px-5 py-3 rounded-none font-display font-bold text-xs uppercase tracking-widest text-center"
                >
                  START HERE
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
